// src/components/shared/SharedUserProfile.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  Settings,
  User,
  LogOut,
  LogIn,
} from "lucide-react";
import { AnalyticsPanel } from './AnalyticsPanel';

const UserAvatar = ({ userProfile, user, size = 8, colour }) => {
  const initial =
    (user?.displayName?.[0] || user?.email?.[0] || "U").toUpperCase();
  const dim = `h-${size} w-${size}`;

  return user?.photoURL ? (
    <img
      src={user.photoURL}
      alt="profile"
      className={`${dim} rounded-full object-cover ring-2 ring-white`}
    />
  ) : (
    <div
      className={`${dim} rounded-full flex items-center justify-center text-white font-medium`}
      style={{ backgroundColor: colour }}
    >
      {initial}
    </div>
  );
};

const UserProfile = ({
  user,
  userProfile,
  styling,
  bookData,
  isOwner,
  actions = {},
  navigate,
  logout,
  isDarkMode,
  setShowAuthModal,
  setShowProfileModal,
  shouldShowUserProfile,
  mobileMenuOpen,
  setMobileMenuOpen,
}) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768
  );
  const [showMenu, setShowMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const showProfile = bookData.headerSettings.showProfile;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 300; // Approximate dropdown height
      
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [showMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const primary = styling?.primaryColor || "#6366f1";
  const textColour =
    bookData?.headerTextColor ||
    bookData?.design?.headerTextColor ||
    bookData?.styling?.headerTextColor;

  if (!user) {
    return (
      <button
        onClick={() => {
          setShowAuthModal(true);
          if (!isDesktop) setMobileMenuOpen(false);
        }}
        className={
          isDesktop
            ? "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            : "w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
        }
        style={{ backgroundColor: primary, color: "#ffffff" }}
      >
        <LogIn className="h-4 w-4" />
        <span>Sign In</span>
      </button>
    );
  }

  if (!isDesktop) {
    return (
      <div className="space-y-2">
        <h4
          className="text-xs bg-slate-900 font-semibold uppercase mb-2"
          style={{ color: textColour }}
        >
          Account
        </h4>

        <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
          {isOwner && bookData?.customDomain && (
            <AnalyticsPanel
              bookId={bookData.id || bookData.bookId}
              compact
            />
          )}

          <UserAvatar
            user={user}
            userProfile={userProfile}
            size={10}
            colour={primary}
          />

          <div className="flex-1 min-w-0">
            <p className={`font-medium truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              {userProfile?.displayName || "User"}
            </p>
            <p className="text-sm text-gray-500 truncate">
              {userProfile?.email || user?.email}
            </p>
          </div>
        </div>

        {isOwner && navigate && (
          <button
            onClick={() => {
              navigate("/dashboard");
              setMobileMenuOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg"
          >
            <Settings className="h-4 w-4" />
            Dashboard
          </button>
        )}

        <button
          onClick={() => {
            setShowProfileModal(true);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg"
          style={{ color: textColour }}
        >
          <User className="h-4 w-4" />
          My Profile
        </button>

        <button
          onClick={() => {
            logout();
            setMobileMenuOpen(false);
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <UserAvatar user={user} userProfile={userProfile} colour={primary} />
        <ChevronDown className="h-4 w-4 text-gray-600 hidden md:block" />
      </button>

      {showMenu && (
        <div 
          ref={dropdownRef}
          className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 ${
            dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {isOwner && bookData?.customDomain && (
            <AnalyticsPanel
              bookId={bookData.id || bookData.bookId}
              compact
            />
          )}

          <div className="p-3 border-b border-gray-200">
            <p className={`font-medium truncate ${isDarkMode ? 'text-slate-400' : 'text-gray-900'}`}>
              {user?.displayName || "User"}
            </p>
            <p className="text-sm text-gray-500 truncate">
              {userProfile?.email || user?.email}
            </p>
          </div>

          <div className="py-1">
            {isOwner && navigate && (
              <>
                <button
                  onClick={() => {
                    navigate("/dashboard");
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Dashboard
                </button>
                <div className="border-t border-gray-200 my-1"></div>
              </>
            )}

            <button
              onClick={() => {
                setShowProfileModal(true);
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              My Profile
            </button>

            <button
              onClick={() => {
                logout();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;